import { nanoid } from "nanoid";
import { DB } from "../lib";
import migrateFrom2 from "./migrations/migrate2";
import { selectWidgets } from "./select";
import { cache, db, WidgetDisplay, WidgetState } from "./state";

export const createId = (): string => nanoid(12);

class WidgetPositionCalculator {
  private static readonly DEFAULT_WIDTH = 200;
  private static readonly DEFAULT_HEIGHT = 50;
  private static readonly VERTICAL_SPACING = 40;
  private static readonly MARGIN = 20;

  /**
   * 计算新插件的智能初始位置
   */
  static calculateInitialPosition(
    existingWidgets: WidgetState[] = [],
    widgetWidth: number = this.DEFAULT_WIDTH,
    widgetHeight: number = this.DEFAULT_HEIGHT
  ): { x: number; y: number; needsRearrangement: boolean; overallOffset?: number } {
    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;

    // 水平居中
    const centerX = Math.max(0, (screenWidth - widgetWidth) / 2);

    if (existingWidgets.length === 0) {
      // 第一个插件：完全居中
      return {
        x: centerX,
        y: Math.max(0, (screenHeight - widgetHeight) / 2),
        needsRearrangement: false
      };
    }

    // 尝试使用中心位置
    const centerY = Math.max(0, (screenHeight - widgetHeight) / 2);
    const centerAvailable = !this.isCenterOccupied(existingWidgets, centerX, screenHeight, widgetHeight) && 
                           !this.wouldOverlap(existingWidgets, centerX, centerY, widgetWidth, widgetHeight);

    if (centerAvailable) {
      // 检查中心位置是否会导致底部溢出
      const willOverflowBottom = centerY + widgetHeight > screenHeight - this.MARGIN;
      if (!willOverflowBottom) {
        return { x: centerX, y: centerY, needsRearrangement: false };
      }
    }

    // 中心不可用，找到最佳位置
    const bestY = this.findBestVerticalPosition(existingWidgets, widgetHeight);
    
    // 检查最佳位置是否会导致底部溢出
    const willOverflowBottom = bestY + widgetHeight > screenHeight - this.MARGIN;

    if (willOverflowBottom) {
      // 计算需要整体上移的偏移量
      const overflowAmount = (bestY + widgetHeight) - (screenHeight - this.MARGIN);
      return {
        x: centerX,
        y: bestY,
        needsRearrangement: true,
        overallOffset: Math.max(0, overflowAmount) // 确保偏移量为非负数
      };
    } else {
      return {
        x: centerX,
        y: bestY,
        needsRearrangement: false
      };
    }
  }

  /**
   * 检查中心区域是否被占用
   */
  private static isCenterOccupied(
    widgets: WidgetState[], 
    centerX: number, 
    screenHeight: number,
    newHeight: number
  ): boolean {
    const centerY = (screenHeight - newHeight) / 2;
    const centerThreshold = 100; // 中心区域的阈值

    return widgets.some(widget => {
      const widgetX = widget.display.x || 0;
      const widgetY = widget.display.y || 0;
      const distanceX = Math.abs(widgetX - centerX);
      const distanceY = Math.abs(widgetY - centerY);
      
      return distanceX < centerThreshold && distanceY < centerThreshold;
    });
  }

  /**
   * 查找最佳的垂直位置
   */
  private static findBestVerticalPosition(widgets: WidgetState[], newHeight: number): number {
    const screenHeight = window.innerHeight;
    const existingPositions = widgets.map(w => w.display.y || 0).sort((a, b) => a - b);
    
    if (existingPositions.length === 0) {
      return Math.max(0, (screenHeight - newHeight) / 2);
    }

    // 计算现有插件的平均垂直位置
    const averageY = existingPositions.reduce((sum, y) => sum + y, 0) / existingPositions.length;
    let candidateY = averageY;

    // 从平均位置开始，向上和向下寻找可用位置
    for (let i = 0; i < 10; i++) {
      const upPosition = candidateY - i * (newHeight + this.VERTICAL_SPACING);
      const downPosition = candidateY + i * (newHeight + this.VERTICAL_SPACING);

      if (upPosition >= this.MARGIN && 
          !this.wouldOverlap(widgets, (window.innerWidth - this.DEFAULT_WIDTH) / 2, upPosition, this.DEFAULT_WIDTH, newHeight)) {
        return upPosition;
      }

      if (downPosition + newHeight <= screenHeight - this.MARGIN && 
          !this.wouldOverlap(widgets, (window.innerWidth - this.DEFAULT_WIDTH) / 2, downPosition, this.DEFAULT_WIDTH, newHeight)) {
        return downPosition;
      }
    }

    // 如果找不到合适位置，使用垂直居中
    return Math.max(0, (screenHeight - newHeight) / 2);
  }

  /**
   * 检查是否会与现有插件重叠
   */
  private static wouldOverlap(
    widgets: WidgetState[], 
    testX: number, 
    testY: number, 
    testWidth: number, 
    testHeight: number
    ): boolean {
      return widgets.some(widget => {
        const existingX = widget.display.x || 0;
        const existingY = widget.display.y || 0;
        const existingWidth = this.DEFAULT_WIDTH;
        const existingHeight = this.DEFAULT_HEIGHT;

        return !(
          testX + testWidth < existingX ||
          testX > existingX + existingWidth ||
          testY + testHeight < existingY ||
          testY > existingY + existingHeight
        );
      });
    }

  /**
   * 重新排列所有插件以实现整体居中，并支持整体上移
   */
  static rearrangeWidgets(widgets: WidgetState[], overallOffset?: number): WidgetState[] {
    if (widgets.length === 0) return widgets;

    const screenWidth = window.innerWidth;
    const screenHeight = window.innerHeight;
    const widgetHeight = this.DEFAULT_HEIGHT;
    const totalHeight = widgets.length * widgetHeight + (widgets.length - 1) * this.VERTICAL_SPACING;
    
    // 计算起始Y位置（居中）
    let startY = Math.max(this.MARGIN, (screenHeight - totalHeight) / 2);
    
    // 如果有整体偏移量，应用上移（允许移出上边界）
    if (overallOffset && overallOffset > 0) {
      startY -= overallOffset;
    }

    const centerX = Math.max(0, (screenWidth - this.DEFAULT_WIDTH) / 2);

    // Skip corner-positioned widgets (topRight, topLeft, etc.) — they shouldn't be dragged to center
    const cornerIds = new Set(widgets.filter(w => w.display.position !== 'middleCentre').map(w => w.id));
    const sorted = widgets.map((widget, index) => ({ widget, index, isCorner: cornerIds.has(widget.id) }));
    let centerIdx = 0;

    return sorted.map(({ widget, isCorner }) => {
      if (isCorner) return widget;
      const next = {
        ...widget,
        display: {
          ...widget.display,
          x: centerX,
          y: startY + centerIdx * (widgetHeight + this.VERTICAL_SPACING)
        }
      };
      centerIdx++;
      return next;
    });
  }

  /**
   * 计算新widget的位置，并在需要时返回整体偏移量
   */
  static calculateLayout(
    existingWidgets: WidgetState[],
    newHeight: number = this.DEFAULT_HEIGHT
  ): { x: number; y: number; overallOffset: number; needsRearrangement: boolean } {
    const screenHeight = window.innerHeight;
    const centerX = Math.max(0, (window.innerWidth - this.DEFAULT_WIDTH) / 2);

    // 计算新widget的理想Y坐标（紧接在最后一个widget下方）
    const idealY = this.calculateIdealY(existingWidgets, newHeight);
    
    // 检查新widget的底部是否会超出屏幕
    const newWidgetBottom = idealY + newHeight;
    const screenBottom = screenHeight - this.MARGIN;
    
    let overallOffset = 0;
    let needsRearrangement = false;

    // 修复：只有当确实会超出时才计算偏移量
    if (newWidgetBottom > screenBottom) {
      // 计算需要整体上移的偏移量
      overallOffset = this.calculateRequiredOffset(existingWidgets, idealY, newHeight, screenHeight);
      needsRearrangement = overallOffset > 0;
    }

    return {
      x: centerX,
      y: idealY, // 返回原始理想位置
      overallOffset,
      needsRearrangement
    };
  }

  /**
   * 计算需要整体上移的偏移量（修复版本）
   */
  private static calculateRequiredOffset(
    widgets: WidgetState[], 
    newWidgetY: number, 
    newHeight: number,
    screenHeight: number
  ): number {
      if (widgets.length === 0) return 0;

      // 计算所有widget（包括新widget）的整体底部位置
      const allWidgetsBottom = newWidgetY + newHeight;
      const maxAllowedBottom = screenHeight - this.MARGIN;
      
      // 如果整体底部不会超出，不需要偏移
      if (allWidgetsBottom <= maxAllowedBottom) {
          return 0;
      }

      // 计算溢出的量 - 直接返回溢出量，不限制上移距离
      const overflowAmount = allWidgetsBottom - maxAllowedBottom;
      return overflowAmount;
  }

  /**
   * 计算新widget应该放置的理想Y坐标
   */
  private static calculateIdealY(
    widgets: WidgetState[], 
    newHeight: number
  ): number {
    if (widgets.length === 0) {
      // 第一个widget：垂直居中
      return Math.max(this.MARGIN, (window.innerHeight - newHeight) / 2);
    }

    // 找到最底部widget的位置
    const bottomMostWidget = widgets.reduce((prev, current) => {
      const prevBottom = (prev.display.y || 0) + this.DEFAULT_HEIGHT;
      const currentBottom = (current.display.y || 0) + this.DEFAULT_HEIGHT;
      return prevBottom > currentBottom ? prev : current;
    });

    const bottomMostY = bottomMostWidget.display.y || 0;
    
    // 新widget放在最底部widget的下方，加上间距
    return bottomMostY + this.DEFAULT_HEIGHT + this.VERTICAL_SPACING;
  }

  /**
   * 重新排列所有widget，应用整体偏移（修复版本）
   */
  static rearrangeWidgetsWithOffset(
    widgets: WidgetState[], 
    offset: number
  ): WidgetState[] {
      if (offset <= 0) return widgets;

      console.log(`应用整体上移: ${offset}px, 影响${widgets.length}个widget`);

      // 修复：按照垂直位置排序，然后统一应用偏移（移除Math.max限制）
      const sortedWidgets = [...widgets].sort((a, b) => (a.display.y || 0) - (b.display.y || 0));
      
      return sortedWidgets.map(widget => {
          const newY = (widget.display.y || 0) - offset; // 移除Math.max限制，允许移出屏幕上边界
          console.log(`Widget ${widget.id}: ${widget.display.y} -> ${newY}`);
          
          return {
              ...widget,
              display: {
                  ...widget.display,
                  y: newY // 允许Y坐标为负值，表示移出屏幕上边界
              }
          };
      });
  } 

  /**
   * 备选方案：使用垂直排列布局（更可靠的方法）
   */
  static arrangeWidgetsVertically(
    widgets: WidgetState[],
    newHeight: number = this.DEFAULT_HEIGHT
  ): WidgetState[] {
      if (widgets.length === 0) return widgets;

      const screenHeight = window.innerHeight;
      const centerX = Math.max(0, (window.innerWidth - this.DEFAULT_WIDTH) / 2);
      
      // 计算总高度（包括间距）
      const totalHeight = widgets.length * newHeight + (widgets.length - 1) * this.VERTICAL_SPACING;
      
      // 计算起始位置（垂直居中） - 移除Math.max限制
      let startY = (screenHeight - totalHeight) / 2; // 允许为负数
      
      // 检查是否会超出底部
      const expectedBottom = startY + totalHeight;
      const screenBottom = screenHeight - this.MARGIN;
      
      // 如果会超出，上移调整（不再限制最小Y值）
      if (expectedBottom > screenBottom) {
          startY -= (expectedBottom - screenBottom);
          // 移除startY = Math.max(this.MARGIN, startY) 限制
      }

      // 按order排序后重新排列
      const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);
      
      return sortedWidgets.map((widget, index) => ({
          ...widget,
          display: {
              ...widget.display,
              x: centerX,
              y: startY + index * (newHeight + this.VERTICAL_SPACING)
          }
      }));
  }

  static calculateLayoutAfterDelete(
        remainingWidgets: WidgetState[],
        deletedWidgetId: string,
        deletedWidgetY: number
    ): { needsReposition: boolean; adjustmentOffset: number } {
        if (remainingWidgets.length === 0) {
            return { needsReposition: false, adjustmentOffset: 0 };
        }

        const screenHeight = window.innerHeight;
        const margin = this.MARGIN;

        // 情况1和2：如果没有插件在窗口外或删除的就是窗口外的插件，直接删除
        if (!this.hasWidgetsAboveViewport(remainingWidgets, screenHeight) || 
            this.isWidgetAboveViewport(deletedWidgetY, screenHeight)) {
            return { needsReposition: false, adjustmentOffset: 0 };
        }

        // 情况3：检查删除的插件是否在窗口内，且上方有滚出窗口的插件
        if (this.isWidgetInViewport(deletedWidgetY, screenHeight) && 
            this.hasWidgetsAboveViewport(remainingWidgets, screenHeight)) {
            
            const adjustmentOffset = this.calculateAdjustmentOffset(remainingWidgets, screenHeight);
            
            // 只有当需要填充有意义的空间时才进行调整
            if (adjustmentOffset > margin) {
                return { needsReposition: true, adjustmentOffset };
            }
        }

        return { needsReposition: false, adjustmentOffset: 0 };
    }

    /**
     * 检查是否有插件在视口上方（部分或全部不可见）
     */
    private static hasWidgetsAboveViewport(widgets: WidgetState[], screenHeight: number): boolean {
        return widgets.some(widget => {
            const widgetY = widget.display.y || 0;
            return widgetY < this.MARGIN; // 插件顶部在视口上方
        });
    }

    /**
     * 检查指定插件是否在视口上方
     */
    private static isWidgetAboveViewport(widgetY: number, screenHeight: number): boolean {
        return widgetY < this.MARGIN;
    }

    /**
     * 检查插件是否在视口内
     */
    private static isWidgetInViewport(widgetY: number, screenHeight: number): boolean {
        const widgetBottom = widgetY + this.DEFAULT_HEIGHT;
        return widgetY >= this.MARGIN && widgetBottom <= screenHeight - this.MARGIN;
    }

    /**
     * 计算需要向下调整的偏移量
     */
    private static calculateAdjustmentOffset(widgets: WidgetState[], screenHeight: number): number {
        // 找到最靠近视口顶部的插件
        const topWidget = widgets.reduce((prev, current) => {
            const prevY = prev.display.y || 0;
            const currentY = current.display.y || 0;
            return prevY < currentY ? prev : current;
        });

        const topWidgetY = topWidget.display.y || 0;
        
        // 如果最顶部插件已经在视口内或很接近视口，不需要调整
        if (topWidgetY >= this.MARGIN) {
            return 0;
        }

        // 计算需要向下移动的距离（让顶部插件至少显示一部分在视口内）
        const requiredOffset = Math.min(Math.abs(topWidgetY), this.DEFAULT_HEIGHT);
        
        return requiredOffset;
    }

    /**
     * 对剩余插件进行位置调整
     */
    static repositionWidgetsAfterDelete(
        widgets: WidgetState[], 
        adjustmentOffset: number
    ): WidgetState[] {
        if (adjustmentOffset <= 0) return widgets;

        console.log(`删除后调整布局，下移插件: ${adjustmentOffset}px`);

        return widgets.map(widget => ({
            ...widget,
            display: {
                ...widget.display,
                y: (widget.display.y || 0) + adjustmentOffset
            }
        }));
    }

    /**
     * 获取删除插件后的紧凑布局（备选方案）
     */
    static getCompactLayoutAfterDelete(widgets: WidgetState[]): WidgetState[] {
        if (widgets.length === 0) return widgets;

        const screenHeight = window.innerHeight;
        const centerX = Math.max(0, (window.innerWidth - this.DEFAULT_WIDTH) / 2);
        
        // 按order排序
        const sortedWidgets = [...widgets].sort((a, b) => a.order - b.order);
        
        // 计算紧凑布局的起始位置
        const totalHeight = sortedWidgets.length * this.DEFAULT_HEIGHT + 
                           (sortedWidgets.length - 1) * this.VERTICAL_SPACING;
        let startY = Math.max(this.MARGIN, (screenHeight - totalHeight) / 2);

        // Preserve corner widgets, only reposition center ones
        const cornerIds = new Set(sortedWidgets.filter(w => w.display.position !== 'middleCentre').map(w => w.id));
        const centerOnly = sortedWidgets.filter(w => !cornerIds.has(w.id));
        let ci = 0;
        return sortedWidgets.map(widget => {
          if (cornerIds.has(widget.id)) return widget;
          const pos = ci++;
          return {
            ...widget,
            display: {
              ...widget.display,
              x: centerX,
              y: startY + pos * (this.DEFAULT_HEIGHT + this.VERTICAL_SPACING)
            }
          };
        });
    }

    static considerCompactLayout = (widgets: WidgetState[]): void => {
      const screenHeight = window.innerHeight;
      const totalHeight = widgets.length * this.DEFAULT_HEIGHT + 
                        (widgets.length - 1) * this.VERTICAL_SPACING;
      
      // 如果总高度远小于屏幕高度，考虑使用紧凑居中布局
      if (totalHeight < screenHeight * 0.6) {
          const compactWidgets = this.getCompactLayoutAfterDelete(widgets);
          
          compactWidgets.forEach(widget => {
              DB.put(db, `widget/${widget.id}`, widget);
          });
          
          console.log(`应用紧凑布局，插件数量: ${widgets.length}`);
      }
    };
}

// Background actions

/** Change the background */
export const setBackground = (key: string): void => {
  const current = DB.get(db, "background");
  const id = createId();
  DB.put(db, "background", {
    id,
    key,
    display: { blur: 0, luminosity: -0.2 },
  });
  DB.del(db, `data/${current.id}`);
  DB.del(cache, current.id);
};

// Widget actions

export const addWidget = (key: string): void => {
    const id = createId();
    const existingWidgets = selectWidgets();
    const order = existingWidgets.length > 0 ?
        existingWidgets[existingWidgets.length - 1].order + 1 : 0;

    const screenW = window.innerWidth;
    const screenH = window.innerHeight;
    const centerX = Math.max(0, (screenW - 200) / 2);

    // Preset Y layers for center-stacked widgets (from top to bottom)
    const yPresets: Record<string, number> = {
      'widget/message':    Math.round(screenH * 0.10), // top of center area
      'widget/greeting':   Math.round(screenH * 0.28), // upper-center
      'widget/time':       Math.round(screenH * 0.42), // center
      'widget/luminaverse': Math.max(40, screenH - 220 - 40), // near bottom, leave breathing room
    };

    const defaultDisplay = (() => {
      if (key === 'widget/weather') {
        return {
          id,
          position: "topRight" as const,
          x: Math.max(16, screenW - 300),
          y: 24,
        };
      }
      // Center stacking with preset Y layers
      const presetY = yPresets[key] ?? Math.round(screenH * 0.45);
      // Find a non-overlapping Y: if preset is too close to an existing center widget, shift down
      const centerWidgets = existingWidgets.filter(w => w.display.position === 'middleCentre');
      let finalY = presetY;
      const minGap = 60;
      for (const w of centerWidgets) {
        if (Math.abs((w.display.y || 0) - finalY) < minGap) {
          finalY = (w.display.y || 0) + 50 + minGap;
        }
      }
      return {
        id,
        position: "middleCentre" as const,
        x: centerX,
        y: Math.max(20, finalY),
      };
    })();

    DB.put(db, `widget/${id}`, {
      id,
      key,
      order,
      display: defaultDisplay,
    } as WidgetState);
    console.log(`添加widget: ${key}, 位置: ${defaultDisplay.x}, ${defaultDisplay.y}`);
}

/** Remove a widget */
export const removeWidget = (id: string): void => {
  const existingWidgets = selectWidgets();
  const widgetToRemove = existingWidgets.find(w => w.id === id);
  
  if (!widgetToRemove) {
      console.log(`Widget with id ${id} not found`);
      return;
  }
  const remainingWidgets = existingWidgets.filter(w => w.id !== id);

  console.log(`删除插件 ${id}, 剩余 ${remainingWidgets.length} 个插件`);

  DB.put(db, `widget/${id}`, null);
  DB.del(db, `data/${id}`);
  DB.del(cache, id);

  // Update order of remaining widgets to keep continuity, don't reposition
  remainingWidgets.forEach(widget => {
    if (widget.order > widgetToRemove.order) {
      DB.put(db, `widget/${widget.id}`, {
        ...widget,
        order: widget.order - 1
      });
    }
  });
};

/** Reorder a widget */
export const reorderWidget = (from: number, to: number): void => {
  const widgets = selectWidgets();
  widgets.splice(to, 0, widgets.splice(from, 1)[0]);
  widgets.forEach((widget, order) =>
    DB.put(db, `widget/${widget.id}`, { ...widget, order }),
  );
};

/** Set display properties of a widget */
export const setWidgetDisplay = (
  id: string,
  display: Partial<WidgetDisplay>,
) => {
  const widget = DB.get(db, `widget/${id}`);
  if (!widget) throw new Error("Widget not found while");
  DB.put(db, `widget/${id}`, {
    ...widget,
    display: { ...widget.display, ...display },
  });
};

// UI actions

/** Toggle dashboard focus mode */
export const toggleFocus = () => {
  DB.put(db, "focus", !DB.get(db, "focus"));
};

// Store actions

/** Import database from a dump */
export const importStore = (dump: any): void => {
  // TODO: Add proper schema validation
  if (typeof dump !== "object" || dump === null)
    throw new TypeError("Unexpected format");

  resetStore();
  if ("backgrounds" in dump) {
    // Version 2 config
    DB.put(db, `widget/default-verse`, null);
    DB.put(db, `widget/default-weather`, null);
    dump = migrateFrom2(dump);
  } else if (dump.version === 3) {
    // Version 3 config
    delete dump.version;
  } else if (dump.version > 3) {
    // Future version
    throw new TypeError("Settings exported from a newer version of LuminaVerse");
  } else {
    // Unknown version
    throw new TypeError("Unknown settings version");
  }
  // @ts-ignore
  Object.entries(dump).forEach(([key, val]) => DB.put(db, key, val));
};

/** Export a database dump */
export const exportStore = (): string => {
  return JSON.stringify({
    ...Object.fromEntries(DB.prefix(db, "")),
    version: 3,
  });
};

/** Reset the database */
export const resetStore = (): void => {
  clear(db);
  clear(cache);
};

const clear = (db: DB.Database): void => {
  // @ts-ignore
  for (const [key] of DB.prefix(db, "")) DB.del(db, key);
};
